import { ChangeDetectionStrategy, Component, computed, inject, input, output, resource, signal } from '@angular/core';
import { DsBoxModule, DsButtonModule, DsMessageModule, DsModalModule, DsToastService } from '@bmw-ds/components';
import { NGXLogger } from 'ngx-logger';

import { RetailCrmService } from '../../../../customer/clients/retail-crm/retail-crm.service';
import {
  CustomerVehicle,
  CustomerVehicleRelationType,
  EditVehicleRequest,
} from '../../../../customer/model/customer-vehicle.model';
import { FoundRetailCustomer } from '../../../../customer/model/found-customer.model';
import { SearchFormService } from '../../../../customer-search-page/search-form.service';
import { SearchResultLineComponent } from '../../../../form/components/search-results/components/search-result/search-result-line.component';
import { useI18n } from '../../../../i18n/services/i18n.service';
import { LoadingIndicatorComponent } from '../../../../loading/loading-indicator.component';
import { ButtonRowComponent } from '../../../../modal/button/button-row.component';
import { usePagination } from '../../../../utils/pagination/pagination.provider';
import { AddRelationModalContainer } from '../../vehicles/add-relation-modal.container';
import { AddCustomerToVehicleSearchContainer } from '../search/add-customer-to-vehicle-search.container';

@Component({
  selector: 'cdf-add-customer-to-vehicle-modal',
  imports: [
    DsBoxModule,
    DsModalModule,
    DsButtonModule,
    AddCustomerToVehicleSearchContainer,
    AddRelationModalContainer,
    SearchResultLineComponent,
    ButtonRowComponent,
    LoadingIndicatorComponent,
    DsMessageModule,
  ],
  providers: [SearchFormService],
  template: `
    @switch (pagination.currentName()) {
      @case ('customer') {
        <cdf-add-customer-to-vehicle-search (selectCustomer)="selectCustomer($event)" />
      }
      @case ('relations') {
        <div class="p-4x d-flex flex-column gap-4x">
          @if (selectedCustomer(); as customer) {
            <cdf-search-result-line box [customer]="customer" />
          }
          @if (customerVehicle(); as customerVehicle) {
            <cdf-add-relation-modal-container
              [vehicle]="customerVehicle"
              [allCustomersOverride]="customerVehicle.allCustomers"
              [(selectedRelations)]="selectedRelations"
            />
          } @else if (vehicleResource.status() === 'loading') {
            <cdf-loading-indicator>{{ i18n('vehicles.addCustomerToVehicle.loadingVehicle') }}</cdf-loading-indicator>
          } @else if (vehicleResource.status() === 'error') {
            <ds-message variant="slim" tone="critical" [messageText]="loadVehicleFailedTemplate" />
            <ng-template #loadVehicleFailedTemplate>
              {{ i18n('vehicles.addCustomerToVehicle.loadVehicleFailed') }}
              <button ds-button variant="outline" size="sm" class="float-end" (click)="vehicleResource.reload()">
                {{ i18n('button.tryAgain') }}
              </button>
            </ng-template>
          }

          <cdf-button-row>
            <button ds-button ds-loading variant="outline" (click)="back()">
              {{ i18n('button.back') }}
            </button>
            <button
              ds-button
              ds-loading
              [isLoading]="loading()"
              [disabled]="selectedRelations().size === 0"
              (click)="addCustomerToVehicle()"
            >
              {{ i18n('vehicles.addCustomerToVehicle.confirmButton') }}
            </button>
          </cdf-button-row>
        </div>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCustomerToVehicleModalContainer {
  protected readonly i18n = useI18n();
  protected readonly logger = inject(NGXLogger);
  protected readonly pagination = usePagination(['customer', 'relations'], () => {
    /* Completion is always handled by addCustomerToVehicle() */
  });
  private readonly retailCrmService = inject(RetailCrmService);
  private readonly toastService = inject(DsToastService);

  readonly vin = input.required<string>();

  readonly cancelModal = output();
  readonly completeModal = output<string>();

  readonly loading = signal(false);

  protected readonly selectedCustomer = signal<FoundRetailCustomer | undefined>(undefined);
  protected readonly selectedRelations = signal<ReadonlySet<CustomerVehicleRelationType>>(new Set());

  protected readonly vehicleResource = resource({
    params: () => this.vin(),
    loader: ({ params: vin }) => this.retailCrmService.getVehicle(vin),
  });

  protected selectCustomer(customer: FoundRetailCustomer) {
    this.selectedCustomer.set(customer);
    this.pagination.goTo('relations');
  }

  protected readonly customerVehicle = computed((): CustomerVehicle | undefined => {
    const vehicle = this.vehicleResource.hasValue() ? this.vehicleResource.value() : undefined;
    if (!vehicle) {
      return undefined;
    }
    const customer = this.selectedCustomer();
    if (!customer) {
      return undefined;
    }
    const relationTypes = Object.values(CustomerVehicleRelationType).filter(
      (r) => vehicle.allCustomers?.[r]?.accountNumber === customer.accountNumber,
    );
    return { ...vehicle, relationTypes };
  });

  private readonly toastId = 'add-customer-to-vehicle-modal-toast';

  protected async addCustomerToVehicle() {
    const customer = this.selectedCustomer();
    if (!customer || this.selectedRelations().size === 0) {
      return;
    }
    this.loading.set(true);
    this.toastService.popToast(this.toastId);

    try {
      const request: EditVehicleRequest = {
        accountNumber: customer.accountNumber,
        vin: this.vin(),
        addRelationTypes: this.selectedRelations(),
        removeRelationTypes: new Set(),
      };

      await this.retailCrmService.updateVehicleC2v(request);
      this.toastService.pushToast({
        id: this.toastId,
        toastTitle: this.i18n('vehicles.addCustomerToVehicle.toastSuccessTitle'),
        toastText: this.i18n('vehicles.addCustomerToVehicle.toastSuccessText'),
        tone: 'positive',
      });
      this.completeModal.emit(customer.accountNumber);
    } catch (error) {
      this.logger.error('#CDFTF0255 Failed to add customer to vehicle', error);
      this.toastService.pushToast({
        id: this.toastId,
        toastTitle: this.i18n('vehicles.addCustomerToVehicle.toastFailedTitle'),
        toastText: this.i18n('vehicles.addCustomerToVehicle.toastFailedText'),
        tone: 'critical',
      });
    } finally {
      this.loading.set(false);
    }
  }

  protected back() {
    this.pagination.back();
    // If the user sees the error message “Please try again later” and clicks the back button,
    // we reload the vehicle so that they can try again without losing their search.
    if (this.vehicleResource.status() === 'error') {
      this.vehicleResource.reload();
    }
  }
}
